import { useState, useRef } from 'react';
import {
    Box,
    Checkbox,
    Flex,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    InputGroup,
    InputRightElement,
    MenuItem,
    Stack,
    Tooltip,
    useBreakpointValue,
    useClipboard,
    useDisclosure,
} from '@chakra-ui/react';
import {
    Button,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Text,
    Input,
} from 'components/v4';
import { LinkIcon } from '@chakra-ui/icons';
import { MdFileCopy } from 'react-icons/md';
import { axios } from '@/services/axios';
import { useSelector } from 'react-redux';

export const GeneratePermission = ({
    accountScope,
    isGCCHService = false,
    serviceObj,
}) => {
    const { isOpen, onOpen, onClose } = useDisclosure();
    const defaultUrl = window.location.origin;
    const [baseUrl, setBaseUrl] = useState(defaultUrl);
    const [redirectUrl, setRedirectUrl] = useState('');
    const [error, setError] = useState('');
    const [isChecked, setIsChecked] = useState(false);
    const [showError, setShowError] = useState(false);
    const { darkMode } = useSelector((state) => state.settings);
    const initialRef = useRef();
    const inputRef = useRef(null);
    const { hasCopied, onCopy } = useClipboard(redirectUrl.data);
    const isMobile = useBreakpointValue({ base: true, md: false });
    const truncatedValue =
        redirectUrl?.data?.length > 55
            ? redirectUrl?.data?.substring(0, 55) + '.....'
            : redirectUrl?.data?.length;
    const handleCheckboxChange = (event) => {
        setIsChecked(event.target.checked);
        setShowError(false);
    };
    const handleUrlChange = (event) => {
        setError('');
        setBaseUrl(event.target.value);
    };
    const handleUrlFocus = () => {
        if (baseUrl === defaultUrl) {
            setBaseUrl('');
        }
    };
    const handleUrlBlur = () => {
        if (baseUrl === '') {
            setBaseUrl(defaultUrl);
        }
    };
    const isValidURL = (string) => {
        let url;
        try {
            url = new URL(string);
        } catch (_) {
            return false;
        }
        return url.protocol === 'http:' || url.protocol === 'https:';
    };
    const generateCopy = () => {
        const urlToUse = baseUrl || defaultUrl;
        let errorMessage = '';
        if (urlToUse.length > 100) {
            errorMessage = `URL must be less than 60 characters example ${defaultUrl}`;
            setError(errorMessage);
            return;
        } else if (!isValidURL(urlToUse)) {
            errorMessage = `Invalid input. Please provide as URL, example ${defaultUrl}`;
            setError(errorMessage);
            return;
        }
        if (!isChecked) {
            setShowError(true);
            return;
        }
        const urlOrigin = new URL(urlToUse);
        const loginLinkPath = isGCCHService
            ? `/Company/${serviceObj?.companyID}/MSGCCHLoginLink?domain=${urlOrigin}`
            : `/Company/${serviceObj?.companyID}/MSLoginLink?domain=${urlOrigin}`;
        axios
            .get(loginLinkPath, {
                headers: {
                    'Cache-Control': 'no-cache',
                    Pragma: 'no-cache',
                    Expires: '0',
                    'X-RequestScope': accountScope,
                },
            })
            .then((res) => setRedirectUrl(res))
            .catch((e) => e);
        // setBaseUrl('');
    };
    const closeAction = () => {
        if (redirectUrl !== '') {
            setRedirectUrl('');
            setIsChecked(false);
        }
        onClose();
    };
    const closeDone = () => {
        setRedirectUrl('');
        onClose();
        setIsChecked(false);
    };

    return (
        <>
            <MenuItem
                id="request-access"
                icon={<LinkIcon color={darkMode ? 'white' : 'black'} />}
                onClick={onOpen}
                data-walkthroughid={`/dashboard-services/card/${serviceObj?.name}/request-access`}>
                <Text marginBottom="0">Request Access</Text>
            </MenuItem>
            <Modal
                blockScrollOnMount={false}
                initialFocusRef={initialRef}
                closeOnOverlayClick={false}
                isOpen={isOpen}
                onClose={closeAction}
                isCentered
                size="xl">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        <span
                            style={{
                                color: 'var(--chakra-colors-messenger-500)',
                            }}>
                            {' '}
                            {serviceObj?.company?.name}
                        </span>
                        -Generate Permission URL
                    </ModalHeader>
                    {redirectUrl === '' && (
                        <>
                            <ModalBody pb={6}>
                                <Text mb="1rem">
                                    Please enter a url to redirect the customer
                                    after permissions have been granted. Leave
                                    empty to use {window.location.origin}.
                                </Text>
                                <FormControl>
                                    <Stack spacing={4}>
                                        <Flex
                                            align="center"
                                            justifyContent="center">
                                            <FormLabel
                                                marginTop={error ? 0 : 2}
                                                sx={
                                                    error
                                                        ? {
                                                              paddingBottom:
                                                                  '17px',
                                                          }
                                                        : {}
                                                }>
                                                Redirect URL
                                            </FormLabel>
                                            <Flex
                                                direction="column"
                                                alignItems="center">
                                                {/* Inner Flex for input and error (column) */}
                                                <FormControl
                                                    isInvalid={!!error}>
                                                    {/* FormControl for styling */}
                                                    <Input
                                                        w={!isMobile && 'sm'}
                                                        ref={initialRef}
                                                        placeholder={defaultUrl}
                                                        value={baseUrl}
                                                        onChange={
                                                            handleUrlChange
                                                        }
                                                        onFocus={handleUrlFocus}
                                                        onBlur={handleUrlBlur}
                                                        data-walkthroughid="resource-url-input"
                                                    />
                                                </FormControl>
                                                {error && (
                                                    <FormHelperText
                                                        data-walkthroughid="resource-url-input-errortext"
                                                        color="red.500">
                                                        {error}
                                                    </FormHelperText>
                                                )}
                                            </Flex>
                                        </Flex>

                                        <Flex align="flex-start" spacing={2}>
                                            {/* Align items to the start (left) */}
                                            <Checkbox
                                                id="terms"
                                                data-walkthroughid="resource-url-checkbox"
                                                size="lg"
                                                isChecked={isChecked}
                                                onChange={handleCheckboxChange}
                                                borderColor={
                                                    showError
                                                        ? 'red.500'
                                                        : undefined
                                                }></Checkbox>
                                            <Box>
                                                <Text fontSize="sm" as="span">
                                                    &nbsp; I acknowledge the
                                                    link generated by this
                                                    process should only be sent
                                                    to an Administrator at{' '}
                                                    <span
                                                        style={{
                                                            color: 'var(--chakra-colors-messenger-500)',
                                                        }}>
                                                        {
                                                            serviceObj?.company
                                                                ?.name
                                                        }
                                                    </span>
                                                    . Sending the wrong url to
                                                    the wrong company may result
                                                    in incorrectly synced data
                                                    requiring the customer to be
                                                    recreated.
                                                </Text>
                                            </Box>
                                        </Flex>
                                        <Flex
                                            align="flex-start"
                                            justifyContent="space-between">
                                            <Button
                                                colorScheme="messenger"
                                                variant="outline"
                                                data-walkthroughid="resource-url-cancel"
                                                width="50%"
                                                onClick={closeAction}
                                                mr={3}>
                                                Cancel
                                            </Button>
                                            <Button
                                                width="50%"
                                                data-walkthroughid="resource-url-submit"
                                                onClick={generateCopy}
                                                colorScheme="messenger">
                                                Submit
                                            </Button>
                                        </Flex>
                                    </Stack>
                                </FormControl>
                            </ModalBody>
                        </>
                    )}
                    {redirectUrl !== '' && (
                        <>
                            <ModalBody pb={6}>
                                <>
                                    <Text fontSize="sm">
                                        Please copy the link below and send to
                                        an {serviceObj?.company?.name}{' '}
                                        adminstrator
                                    </Text>
                                </>
                                <Stack spacing={4}>
                                    <InputGroup>
                                        <Tooltip
                                            label={redirectUrl.data}
                                            placement="top">
                                            <Input
                                                ref={inputRef}
                                                value={truncatedValue}
                                                isReadOnly
                                            />
                                        </Tooltip>
                                        <InputRightElement>
                                            <Tooltip
                                                label={
                                                    hasCopied
                                                        ? 'Copied!'
                                                        : 'Copy to clipboard'
                                                }
                                                placement="top">
                                                <IconButton
                                                    sx={{
                                                        fontSize: '20px',
                                                        color: '#6b6b6b',
                                                        margin: 0,
                                                        _hover: {
                                                            bg: '#c4c4c4',
                                                        },
                                                    }}
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={onCopy}
                                                    icon={<MdFileCopy />}
                                                />
                                            </Tooltip>
                                        </InputRightElement>
                                    </InputGroup>
                                    <ModalFooter pr={0}>
                                        <Button
                                            data-walkthroughid="resource-url-done"
                                            onClick={closeDone}>
                                            Done
                                        </Button>
                                    </ModalFooter>
                                </Stack>
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </>
    );
};
